import { catchError, of, forkJoin, tap, EMPTY } from 'rxjs'
import { Injectable } from '@angular/core'
import { BaseResponse } from '@services/api/api-base.service'
import { HttpClient, HttpParams } from '@angular/common/http'
import { emptyPaginateObservable, Paginate, ofPaginate } from '@models/paginate.model'
import { ManagementApiBaseService } from '@services/api/management-api-base.service'
import { map, switchMap } from 'rxjs/operators'
import {
	SupplierEnrollmentNotice,
	SupplierEnrollmentNoticeSearch,
	SupplierEnrollmentNoticeJudging,
	SupplierEnrollmentNoticeUndoStatus,
	SupplierEnrollmentNoticeAttachmentType,
	SupplierEnrollmentNoticeStatus
} from '@models/supplier/notice/supplier-enrollment-notice.model'
import { CustomerService } from '@services/customer.service'
import { CompanySize } from '@models/person/company.model'
import { Organization } from '@models/account.model'
import { Retry } from '@services/decorators/retry.decorator'
import { NoticeSession, NoticeSessionStatus } from '@models/planning/notice-session.model'
import moment from 'moment/moment'
import { SmallBusinessDeclarationState, SupplierEnrollmentNoticeDeclarationType } from '@models/supplier/notice/supplier-enrollment-notice-declaration.model'
import { EnrollmentPublicRecord } from '@models/supplier/notice/enrollment-public-record.model'

@Injectable({
	providedIn: 'root'
})
export class SupplierEnrollmentNoticeService extends ManagementApiBaseService<SupplierEnrollmentNoticeSearch> {
	private organizationCache = new Map<string, Organization>()

	private readonly source: string

	constructor(
		override http: HttpClient,
		private customerService: CustomerService
	) {
		super(http)
		this.sortColumn = 'createdAt'
		this.source = 'supplier-enrollment-notices'
		this.searchTerm = new SupplierEnrollmentNoticeSearch()
	}

	/**
	 * Cria a inscrição do fornecedor na sessão. `companySize` é a declaração de porte exigida em
	 * inscrição NOVA (spec 039 / 027 do backend — o servidor recusa a criação sem ela); no reclique
	 * de inscrição existente o servidor ignora o campo e não re-declara.
	 */
	enrollment(id: string, companySize: CompanySize) {
		return this.post<any, BaseResponse>(`${this.source}`, { demand: { id }, companySize })
	}

	/** Declaração ME/EPP da inscrição: o texto vigente e o que já foi declarado (spec 045, requisito 14). */
	smallBusinessDeclaration(id: string) {
		return this.get<SmallBusinessDeclarationState>(`${this.source}/${id}/small-business-declaration`).pipe(
			map(response => new SmallBusinessDeclarationState(response))
		)
	}

	/**
	 * Confirma a declaração ME/EPP com as versões do texto mostrado: com o texto atualizado no meio do caminho, o
	 * servidor recusa e o participante relê.
	 */
	declareSmallBusiness(id: string, state: SmallBusinessDeclarationState) {
		return this.put<any, SmallBusinessDeclarationState>(`${this.source}/${id}/small-business-declaration`, {
			frameworkVersion: state.versionOf(SupplierEnrollmentNoticeDeclarationType.SMALL_BUSINESS_FRAMEWORK),
			revenueLimitVersion: state.versionOf(SupplierEnrollmentNoticeDeclarationType.EPP_REVENUE_LIMIT)
		}).pipe(map(response => new SmallBusinessDeclarationState(response)))
	}

	searchSession(sessionId: string, params?: HttpParams, query?: SupplierEnrollmentNoticeSearch) {
		query = query ?? new SupplierEnrollmentNoticeSearch()

		return this.post<SupplierEnrollmentNoticeSearch, Paginate<SupplierEnrollmentNotice>>(
			`${this.source}/${sessionId}/search`,
			query,
			this.params(params)
		).pipe(
			map(response => ofPaginate(response, SupplierEnrollmentNotice)),
			catchError(() => emptyPaginateObservable<SupplierEnrollmentNotice>())
		)
	}

	/**
	 * Documento da inscrição de outro participante (proposta inicial e documentos de habilitação), depois da abertura da
	 * disputa. Devolve a URL assinada que o management libera; sem inscrição na sessão, a rota responde 404.
	 */
	participantAttachmentDownload(sessionId: string, tenantId: string, attachmentId: string) {
		return this.get<{ preSignedUrl: string }>(`${this.source}/${sessionId}/${tenantId}/attachments/${attachmentId}/download`)
	}

	/** A proposta inicial (comprovante em PDF) de outro participante, pelo id da inscrição, como o quadro do agente baixa. */
	participantProposalAttachmentDownload(sessionId: string, tenantId: string, enrollmentId: string) {
		return this.get<{ preSignedUrl: string }>(`${this.source}/${sessionId}/${tenantId}/proposals/${enrollmentId}/download`)
	}

	/** Os atos públicos de outro participante da disputa, depois da abertura da sessão (spec 067). Sem inscrição na sessão, 404. */
	participantRecord(sessionId: string, tenantId: string, enrollmentId: string) {
		return this.get<EnrollmentPublicRecord>(`${this.source}/${sessionId}/${tenantId}/records/${enrollmentId}`).pipe(
			map(response => new EnrollmentPublicRecord(response))
		)
	}

	/** O anexo de um desses atos: o management só libera o que a leitura acima devolveu. */
	participantRecordAttachmentDownload(sessionId: string, tenantId: string, enrollmentId: string, attachmentId: string) {
		return this.get<{ preSignedUrl: string }>(`${this.source}/${sessionId}/${tenantId}/records/${enrollmentId}/attachments/${attachmentId}/download`)
	}

	searchSessionTenantId(sessionId: string, tenantId: string, params?: HttpParams, query?: SupplierEnrollmentNoticeSearch) {
		query = query ?? new SupplierEnrollmentNoticeSearch()

		return this.post<SupplierEnrollmentNoticeSearch, Paginate<SupplierEnrollmentNotice>>(
			`${this.source}/${sessionId}/${tenantId}/search`,
			query,
			this.params(params)
		).pipe(
			map(response => ofPaginate(response, SupplierEnrollmentNotice)),
			catchError(() => emptyPaginateObservable<SupplierEnrollmentNotice>())
		)
	}

	search(params?: HttpParams, query?: SupplierEnrollmentNoticeSearch) {
		query = query ?? new SupplierEnrollmentNoticeSearch()

		if (query.status) {
			query.statuses = [query.status]
		}

		if (query.term && query.term.label) {
			query.term = query.term.label
		}

		delete query.status

		return this.post<SupplierEnrollmentNoticeSearch, Paginate<SupplierEnrollmentNotice>>(`${this.source}/search`, query, this.params(params)).pipe(
			switchMap(response => {
				const paginate = ofPaginate(response, SupplierEnrollmentNotice)

				if (paginate.empty) {
					return of(response)
				}

				return forkJoin(
					paginate.content.map(it => {
						const tenantId = it.noticeSession.tenantId

						if (this.organizationCache.has(tenantId)) {
							it.organization = this.organizationCache.get(tenantId)!
							return of(it)
						}

						return this.customerService.findById(tenantId).pipe(
							tap(org => this.organizationCache.set(tenantId, org)),
							map(organization => {
								it.organization = new Organization(organization)
								return it
							})
						)
					})
				).pipe(
					map(updatedContent => {
						paginate.content = updatedContent ?? []
						return paginate
					})
				)
			}),
			catchError(() => emptyPaginateObservable<SupplierEnrollmentNotice>())
		)
	}

	judging(entity: SupplierEnrollmentNoticeJudging) {
		return this.post<SupplierEnrollmentNoticeJudging, BaseResponse>(`${this.source}/judging`, entity)
	}

	detail(id?: string) {
		return this.get<SupplierEnrollmentNotice>(`${this.source}/${id}`).pipe(map(response => new SupplierEnrollmentNotice(response)))
	}

	validateInDispute(supplierEnrollmentNotice: SupplierEnrollmentNotice, trading = false) {
		const verifyStatusExcepted = [NoticeSessionStatus.CREATED, NoticeSessionStatus.CLOSED]
		const fallbackNoticeSession = supplierEnrollmentNotice?.noticeSession

		return this.detail(supplierEnrollmentNotice?.id).pipe(
			map(response => {
				const noticeSession = (response?.noticeSession ?? fallbackNoticeSession) as NoticeSession

				if (!noticeSession) {
					return false
				}

				const statuses = [SupplierEnrollmentNoticeStatus.CREATED, SupplierEnrollmentNoticeStatus.PROPOSAL_SENT]

				if (!statuses.includes(response.status) && !trading) {
					return false
				}

				if (verifyStatusExcepted !== null && verifyStatusExcepted.length > 0) {
					if (!verifyStatusExcepted.includes(noticeSession.status)) {
						return false
					}
				}

				const closedDate = noticeSession.closedDate
				const closedTime = noticeSession.closedTime

				if (closedDate && closedTime) {
					const expiresAt = moment(`${closedDate} ${closedTime}`).subtract(1, 'minute')
					const instant = moment()

					if (instant.isSame(expiresAt) || instant.isAfter(expiresAt)) {
						return false
					}
				}

				return true
			}),
			catchError(() => of(false))
		)
	}

	@Retry(3)
	undo(id: string, action: SupplierEnrollmentNoticeUndoStatus) {
		return this.patch(`${this.source}/${id}/undo`, { action }).pipe(catchError(() => EMPTY))
	}

	@Retry(3)
	qualificationAttachmentConfirm(id: string) {
		return this.patch<any>(`${this.source}/${id}/qualification-attachment-confirmed`).pipe(catchError(() => EMPTY))
	}

	@Retry(3)
	proposalAttachmentConfirm(id: string) {
		return this.patch<any>(`${this.source}/${id}/proposal-attachment-confirmed`).pipe(catchError(() => EMPTY))
	}

	@Retry(3)
	appealConfirm(id: string, description?: string) {
		return this.patch<any>(`${this.source}/${id}/appeal-confirmed`, { description }).pipe(catchError(() => EMPTY))
	}

	@Retry(3)
	certificatedConfirm(id: string) {
		return this.patch<any>(`${this.source}/${id}/certificated-confirmed`).pipe(catchError(() => EMPTY))
	}

	@Retry(3)
	unpublishConfirmed(id: string) {
		return this.patch(`${this.source}/${id}/unpublish-confirmed`).pipe(catchError(() => EMPTY))
	}

	/**
	 * Desistência depois da abertura da sessão (PC-06): só o dono, com o motivo. Sem `Retry` nem `catchError`: a tela
	 * precisa saber da recusa (o interceptor já mostra a mensagem do servidor), e repetir registraria o mesmo ato.
	 */
	withdraw(id: string, justification: string) {
		return this.patch(`${this.source}/${id}/withdrawal`, { justification })
	}

	/** `qualificationId` (spec 043): o documento responde a uma solicitação de habilitação — o agente o vê no julgamento dela. */
	@Retry(3)
	attachmentsSave(id: string, type: SupplierEnrollmentNoticeAttachmentType, qualificationId?: string) {
		return this.post<any, BaseResponse>(`${this.source}/attachments`, qualificationId ? { id, type, qualificationId } : { id, type })
	}

	/**
	 * Remoção de documento de habilitação (spec 047): só na inversão de fases e antes da abertura da sessão.
	 * O erro não é engolido — a recusa do servidor (REJECTED_1074) chega à tela com a mensagem dele.
	 */
	@Retry(3)
	attachmentsDelete(id: string) {
		return this.delete(`${this.source}/attachments/${id}`)
	}

	@Retry(3)
	attachmentsConfirmed(id: string) {
		return this.patch(`${this.source}/attachments/${id}/confirmed`).pipe(catchError(() => EMPTY))
	}
}
